// src/parsers/excellon.ts

import type { DrillHit, ParseError, Result } from '../types';

/**
 * Excellon drill file parser
 * Supports both Excellon 1 and Excellon 2 formats
 * 
 * TODO: Implement full Excellon parsing
 * - Parse header (M48) with tool definitions
 * - Parse coordinate data (M95/% end of header)
 * - Handle absolute vs incremental coordinates
 * - Handle metric vs imperial units
 * - Generate drill hits with positions and tool info
 */

export interface ExcellonParseOptions {
  unit?: 'mm' | 'inch';
}

export interface ExcellonParseResult {
  drills: DrillHit[];
  warnings: ParseError[];
  unit: 'mm' | 'inch';
  tools: Map<number, { diameter: number; plated: boolean }>;
}

export async function parseExcellonFile(
  content: string,
  filename: string,
  options: ExcellonParseOptions = {}
): Promise<Result<ExcellonParseResult, ParseError[]>> {
  const warnings: ParseError[] = [];
  const lines = content.split(/\r?\n/);
  
  let inHeader = false;
  let headerEnded = false;
  let unit: 'mm' | 'inch' = options.unit || 'inch';
  let currentTool: number | null = null;
  
  const tools = new Map<number, { diameter: number; plated: boolean }>();
  const drills: DrillHit[] = [];
  
  try {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines and comments
      if (!line || line.startsWith(';')) continue;
      
      // Header start
      if (line === 'M48') {
        inHeader = true;
        continue;
      }
      
      // Header end
      if (line === 'M95' || line === '%') {
        inHeader = false;
        headerEnded = true;
        continue;
      }
      
      if (inHeader) {
        // Parse header commands
        // Unit specification
        if (line.startsWith('METRIC')) {
          unit = 'mm';
        } else if (line.startsWith('INCH')) {
          unit = 'inch';
        }
        
        // Tool definition
        const toolMatch = line.match(/^T(\d+)(?:C([\d.]+))?(?:F([\w]+))?/);
        if (toolMatch) {
          const toolNum = parseInt(toolMatch[1], 10);
          const diameter = toolMatch[2] ? parseFloat(toolMatch[2]) : 0;
          const flags = toolMatch[3] || '';
          
          tools.set(toolNum, {
            diameter,
            plated: !flags.includes('NP') // NP = non-plated
          });
        }
      } else if (headerEnded) {
        // Parse coordinate data
        // Tool change
        const toolChangeMatch = line.match(/^T(\d+)$/);
        if (toolChangeMatch) {
          currentTool = parseInt(toolChangeMatch[1], 10);
          continue;
        }
        
        // Coordinate
        const coordMatch = line.match(/^X?([+-]?\d+)Y?([+-]?\d+)$/);
        if (coordMatch && currentTool !== null) {
          const x = parseInt(coordMatch[1], 10);
          const y = parseInt(coordMatch[2], 10);
          
          // Convert to mm (Excellon uses 4.4 or 4.6 format)
          // Assume 4.4 format (4 digits before, 4 after decimal)
          const scale = unit === 'mm' ? 0.0001 : 0.0001 * 25.4;
          
          const tool = tools.get(currentTool);
          
          drills.push({
            x: x * scale,
            y: y * scale,
            diameter: tool?.diameter || 0,
            plated: tool?.plated ?? true,
            toolNumber: currentTool
          });
        }
      }
    }
    
    return {
      success: true,
      data: {
        drills,
        warnings,
        unit,
        tools
      }
    };
  } catch (error) {
    return {
      success: false,
      error: [{
        file: filename,
        message: error instanceof Error ? error.message : 'Unknown error',
        severity: 'error'
      }]
    };
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Detect if file is Excellon format
 */
export function isExcellonFormat(content: string): boolean {
  const sample = content.slice(0, 500).trim();
  
  // Check for Excellon header
  if (sample.includes('M48')) return true;
  
  // Check for tool definitions
  if (/T\d+C[\d.]+/.test(sample)) return true;
  
  // Check for coordinate format
  const coordLines = sample.split('\n').filter(l => /^X?\d+Y?\d+$/.test(l.trim()));
  if (coordLines.length > 0) return true;
  
  return false;
}

/**
 * Merge drill files (plated and unplated)
 */
export function mergeDrillFiles(results: ExcellonParseResult[]): DrillHit[] {
  const allDrills: DrillHit[] = [];
  
  for (const result of results) {
    allDrills.push(...result.drills);
  }
  
  // Sort by position for consistent rendering
  return allDrills.sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });
}
